import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Sparkles, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0B0F19]">
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-r border-white/5">
        <img src="https://images.unsplash.com/photo-1550602883-4c2d2c705db2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F19] via-[#0B0F19]/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-16">
          <h1 className="font-display font-extrabold text-5xl text-white leading-[1.05] max-w-lg tracking-tight">
            A curated intelligence archive of your work.
          </h1>
          <p className="text-slate-400 mt-6 max-w-md text-lg">
            Upload anything. Our AI reads, understands, organizes and assembles it into an executive-ready portfolio — with full provenance and source citations.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-[480px] flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-white">Work Intelligence</div>
              <div className="text-[11px] text-slate-500 tracking-wide">PORTFOLIO ARCHIVE</div>
            </div>
          </div>

          <h2 className="font-display text-2xl font-bold text-white mb-1">Secure sign in</h2>
          <p className="text-sm text-slate-500 mb-8">Access is private and role-based.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 w-full bg-[#070B14] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1.5 w-full bg-[#070B14] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="login-submit" disabled={loading} type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium rounded-lg px-4 py-2.5 transition-colors flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
            </button>
          </form>

          <p className="mt-8 text-xs text-slate-600 border-t border-white/5 pt-5">
            Access is restricted to authorized users. Contact your administrator for an account.
          </p>
        </div>
      </div>
    </div>
  );
}
